/**
 * TUNING COERCION — the shared rules the live-tuning setters use to turn an untrusted blob into game data.
 *
 * Every value a setter installs comes from the `tuning` row in Postgres: written by the dev-menu editors, by
 * `scripts/tune.mjs`, by the assistant, or by hand. None of those is typed, and a spread like
 * `{ ...DEFAULTS, ...raw }` carries a string `"14"` straight into arithmetic (`"14" + 0.3 * 16` is the string
 * `"144.8"`), a `null` into a gap that then reads as 0, and an unknown rarity into a table lookup that returns
 * undefined and throws one property later. The achievements overlay (`rewardCount` in sim/achievements.ts) set
 * the standard: every field is typed on the way in, and anything that fails falls back to the authored value.
 * This module is the same rule for the spawn sims (insectSpawn / fishSpawn / *Rarity / *Conditions).
 *
 * THE NULL RULE. A `null` ENTRY in a per-id map is meaningful where its setter says so (a condition override of
 * `null` means "no conditions"), so `coerceCondMap` keeps it. A null FIELD is never meaningful — it is dropped
 * and the authored value stands.
 */

/** A plain object (not null, not an array). */
export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/** `v` if it is a finite number, held to at least `floor`; `dflt` for anything else (strings, NaN, null…). */
export function finiteAtLeast(v: unknown, floor: number, dflt: number): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.max(floor, v) : dflt;
}

/** A per-rarity 0..1 table (`tierBasePct` / `tierCap`): every tier present, each a finite fraction. A missing,
 *  non-numeric or non-finite tier keeps the default; an out-of-range one is clamped into 0..1. Unknown keys are
 *  dropped, so the table cannot grow a tier no lookup asks for. */
export function coerceTierTable<R extends string>(raw: unknown, dflt: Record<R, number>): Record<R, number> {
  const o = isPlainObject(raw) ? raw : {};
  const out = { ...dflt };
  for (const k of Object.keys(dflt) as R[]) {
    const v = o[k];
    if (typeof v === "number" && Number.isFinite(v)) out[k] = Math.max(0, Math.min(1, v));
  }
  return out;
}

/** A per-id RARITY override map (`insectRarity` / `fishRarity`). An entry keeps only a `rarity` that is one of
 *  the real tiers and a `basePct` that is a finite number (clamped to 0..1); an entry left with neither is
 *  dropped, as is any entry that is not an object. A tier outside the list would make `RARITY_CATCH[tier]`
 *  undefined and throw on the next catch. */
export function coerceRarityMap<R extends string>(
  raw: unknown, tiers: readonly R[],
): Record<string, { rarity?: R; basePct?: number }> {
  const out: Record<string, { rarity?: R; basePct?: number }> = {};
  if (!isPlainObject(raw)) return out;
  for (const [id, e] of Object.entries(raw)) {
    if (!isPlainObject(e)) continue;
    const r: { rarity?: R; basePct?: number } = {};
    if (tiers.includes(e.rarity as R)) r.rarity = e.rarity as R;
    if (typeof e.basePct === "number" && Number.isFinite(e.basePct)) r.basePct = Math.max(0, Math.min(1, e.basePct));
    if (r.rarity !== undefined || r.basePct !== undefined) out[id] = r;
  }
  return out;
}

/** Which condition fields carry which type. Every field NOT named here is numeric. */
export interface CondSpec {
  bools: readonly string[];
  strings: readonly string[];
  stringArrays: readonly string[];
}

/** One condition object, field by field: booleans stay booleans, ids stay non-empty strings, `weather` /
 *  `flowers` stay arrays of strings, `sets` stays an array of objects, and everything else must be a finite
 *  number. A field of the wrong type is DROPPED — `{ weather: 5 }` would otherwise throw at
 *  `c.weather.includes`, and `{ flowers: 5 }` at `c.flowers.join` in the gate message. */
export function coerceCond<T>(raw: Record<string, unknown>, spec: CondSpec): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (spec.bools.includes(k)) { if (typeof v === "boolean") out[k] = v; continue; }
    if (spec.strings.includes(k)) { if (typeof v === "string" && v) out[k] = v; continue; }
    if (spec.stringArrays.includes(k)) {
      if (Array.isArray(v)) out[k] = v.filter((x) => typeof x === "string");
      continue;
    }
    if (k === "sets") { if (Array.isArray(v)) out[k] = v.filter(isPlainObject); continue; }
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  return out as T;
}

/** A per-id CONDITION override map (`insectConditions` / `fishConditions`). A `null` entry is KEPT — it is the
 *  admin's explicit "no conditions". An object entry is coerced field by field; any other entry (a number, a
 *  string, an array) is dropped, so that species keeps its authored condition. */
export function coerceCondMap<T>(raw: unknown, spec: CondSpec): Record<string, T | null> {
  const out: Record<string, T | null> = {};
  if (!isPlainObject(raw)) return out;
  for (const [id, e] of Object.entries(raw)) {
    if (e === null) out[id] = null;
    else if (isPlainObject(e)) out[id] = coerceCond<T>(e, spec);
  }
  return out;
}
